import { ref } from 'vue'
import { createFetch } from '@vueuse/core'

/**
 * Fetch for the Checkout API, which needs the landing-page session and a CSRF token on every
 * request (AIRLST-5717). The token comes from the page's csrf-token meta tag, like the CenterStage
 * axios setup; landing pages never get the XSRF-TOKEN cookie that '@/composables/useFetch' reads.
 *
 * 401 (the session holds no checkout for this site) and 419 (the session expired or the token does
 * not match) set checkoutSessionExpired. CheckoutPage then shows its "expired" dialog, because only
 * a reload of the page can start a new session.
 */
export const checkoutSessionExpired = ref(false)

const SESSION_EXPIRED_STATUSES = [401, 419]

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ??
  ''

export const useFetch = createFetch({
  options: {
    updateDataOnError: true,
    async beforeFetch({ options }) {
      options.headers['Content-Type'] = 'application/json'
      options.headers.Accept = 'application/json'
      options.headers['X-CSRF-TOKEN'] = csrfToken()

      return { options }
    },
    onFetchError(ctx) {
      if (SESSION_EXPIRED_STATUSES.includes(ctx.response?.status)) {
        checkoutSessionExpired.value = true
      }

      return ctx
    },
  },
})

export const getJson = (url, options) =>
  useFetch(url, options ?? {})
    .get()
    .json()

export const postJson = (url, payload, options) =>
  useFetch(url, options ?? {})
    .post(payload ?? {})
    .json()

export const putJson = (url, payload, options) =>
  useFetch(url, options ?? {})
    .put(payload ?? {})
    .json()

export const patchJson = (url, payload, options) =>
  useFetch(url, options ?? {})
    .patch(payload ?? {})
    .json()

export const deleteJson = (url, payload, options) =>
  useFetch(url, options ?? {})
    .delete(payload ?? {})
    .json()
